// src/components/Trainer/ShareBoard.jsx
import { useTranslation } from "../../i18n";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './ShareBoard.css';
import API_BASE_URL from '../../config/api';

const API_URL = API_BASE_URL;

// valida ObjectId 24 hex
const isObjectId = (v) => /^[a-f\d]{24}$/i.test(String(v || '').trim());

const safeJson = async (resp) => {
  try {
    return await resp.json();
  } catch {
    return null;
  }
};

// ✅ Mapea lo que venga (running/cycling/...) a lo que tu backend acepta
const normalizeType = (raw) => {
  const v = String(raw || '').toLowerCase().trim();
  const valid = new Set(['correr', 'ciclismo', 'nadar', 'fuerza']);
  if (valid.has(v)) return v;

  const map = {
    running: 'correr',
    run: 'correr',
    trail: 'correr',
    jogging: 'correr',

    cycling: 'ciclismo',
    bike: 'ciclismo',
    ride: 'ciclismo',
    ciclismo: 'ciclismo',

    swimming: 'nadar',
    swim: 'nadar',
    natacion: 'nadar',

    strength: 'fuerza',
    gym: 'fuerza',
    workout: 'fuerza',
  };

  return map[v] || 'correr';
};

// ✅ keys para compatibilidad
const DND_KEY_NEW = 'application/tzone-event';
const DND_KEY_OLD = 'application/x-tzone-event';

export default function ShareBoard() {
  const { t } = useTranslation();
  const [athletes, setAthletes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const [overId, setOverId] = useState(null);
  const [sharingAthleteId, setSharingAthleteId] = useState(null);

  // fallback sin DnD
  const [selectedEventId, setSelectedEventId] = useState(null);

  // log visible en pantalla
  const [logs, setLogs] = useState([]);

  const userId = useMemo(() => {
    return (
      localStorage.getItem('userId') ||
      localStorage.getItem('userID') ||
      ''
    ).trim();
  }, []);

  const pushLog = useCallback((msg, extra) => {
    const line =
      `[${new Date().toLocaleTimeString('es-CL')}] ${msg}` +
      (extra ? ` :: ${extra}` : '');
    setLogs((prev) => [line, ...(prev || [])].slice(0, 12));
  }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.clearTimeout(window.__sb_toast);
    window.__sb_toast = window.setTimeout(() => setToast(''), 2500);
  }, []);

  const buildShareEvent = useCallback((rawEvent) => {
    return {
      ...rawEvent,
      // ✅ enum backend
      type: normalizeType(rawEvent?.type || rawEvent?.sport),
      // sport lo dejamos como venía (tu curl funcionó así)
      sport: String(rawEvent?.sport || '').toLowerCase().trim() || 'running',
      planned: {
        ...(rawEvent?.planned || {}),
        durationMin: Number(
          rawEvent?.planned?.durationMin || rawEvent?.durationMin || 60
        ),
        tss: Number(rawEvent?.planned?.tss || 0),
      },
    };
  }, []);

  const shareToAthlete = useCallback(
    async (athleteId, rawEvent) => {
      const event = buildShareEvent(rawEvent);

      const res = await fetch(`${API_URL}/api/training/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({ athleteId, event }),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        throw new Error(data?.message || `HTTP ${res.status}`);
      }
      return data;
    },
    [userId, buildShareEvent]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    // ✅ eventos demo para probar
    const demo = [
      {
        id: 't1',
        title: 'Rodaje Z2',
        start: new Date(Date.now() + 3600_000).toISOString(),
        planned: { durationMin: 60, tss: 45 },
        type: 'running',
        sport: 'running',
      },
      {
        id: 't2',
        title: 'Series (VO2)',
        start: new Date(Date.now() + 7200_000).toISOString(),
        planned: { durationMin: 50, tss: 65 },
        type: 'running',
        sport: 'running',
      },
      {
        id: 't3',
        title: 'Bike Z2',
        start: new Date(Date.now() + 5400_000).toISOString(),
        planned: { durationMin: 90, tss: 70 },
        type: 'cycling',
        sport: 'cycling',
      },
    ];
    setEvents(demo);
    setSelectedEventId((prev) => prev || demo[0]?.id || null);

    try {
      if (!isObjectId(userId)) {
        setAthletes([]);
        const msg =
          `Tu userId en localStorage no es válido (no es ObjectId). ` +
          `Cierra sesión y vuelve a iniciar para que se guarde un _id real.`;
        setError(msg);
        pushLog('userId inválido', String(userId || ''));
        return;
      }

      const aRes = await fetch(`${API_URL}/api/community/my-athletes`, {
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
      });

      const aJson = await safeJson(aRes);

      if (!aRes.ok) {
        const msg = aJson?.message || `HTTP ${aRes.status}`;
        throw new Error(msg);
      }

      const list = Array.isArray(aJson) ? aJson : aJson?.athletes || [];
      setAthletes(Array.isArray(list) ? list : []);
      pushLog('Atletas cargados', `count=${Array.isArray(list) ? list.length : 0}`);
    } catch (e) {
      setAthletes([]);
      const msg = String(e?.message || e);
      setError(msg);
      pushLog('Error cargando atletas', msg);
    } finally {
      setLoading(false);
    }
  }, [userId, pushLog]);

  useEffect(() => {
    load();
  }, [load]);

  const onDragStartEvent = (e, ev) => {
    e.dataTransfer.setData('text/plain', ev.title || 'evento');

    const payload = JSON.stringify(ev);
    e.dataTransfer.setData(DND_KEY_NEW, payload);
    e.dataTransfer.setData(DND_KEY_OLD, payload);

    e.dataTransfer.effectAllowed = 'copy';
    showToast(`🟦 Arrastrando: ${ev.title}`);
    pushLog('DragStart', ev.title);
  };

  const extractDraggedEvent = (e) => {
    const raw =
      e.dataTransfer.getData(DND_KEY_NEW) ||
      e.dataTransfer.getData(DND_KEY_OLD);

    if (!raw) return null;
    try {
      const ev = JSON.parse(raw);
      return ev && ev.title ? ev : null;
    } catch {
      return null;
    }
  };

  const onDropOnAthlete = async (e, athlete) => {
    e.preventDefault();
    e.stopPropagation();
    setOverId(null);

    const athleteId = athlete.userId || athlete._id || athlete.id;
    const name =
      athlete.displayName || athlete.name || athlete.fullName || 'Atleta';

    if (!isObjectId(athleteId)) {
      showToast('❌ athleteId inválido (no es ObjectId).');
      pushLog('Drop (fail)', `athleteId inválido: ${String(athleteId || '')}`);
      return;
    }

    const ev = extractDraggedEvent(e);
    if (!ev) {
      showToast('❌ Drop recibido, pero SIN payload de evento.');
      pushLog('Drop (fail)', 'sin payload DnD');
      return;
    }

    showToast(`📨 Enviando "${ev.title}" a ${name}...`);
    pushLog('Drop', `"${ev.title}" → ${name}`);
    setSharingAthleteId(String(athleteId));

    try {
      const resp = await shareToAthlete(athleteId, ev);
      showToast(`✅ Compartido: ${ev.title} → ${name}`);
      pushLog('OK /training/share', `id=${resp?.id || resp?._id || 'ok'}`);
    } catch (err) {
      const msg = String(err?.message || err);
      showToast(`❌ ${msg}`);
      pushLog('ERROR /training/share', msg);
    } finally {
      setSharingAthleteId(null);
    }
  };

  // fallback SIN DnD
  const selectedEvent = useMemo(() => {
    return (events || []).find((x) => String(x.id) === String(selectedEventId)) || null;
  }, [events, selectedEventId]);

  const shareSelectedTo = async (athlete) => {
    const athleteId = athlete.userId || athlete._id || athlete.id;
    const name =
      athlete.displayName || athlete.name || athlete.fullName || 'Atleta';

    if (!selectedEvent) {
      showToast('Selecciona un evento primero.');
      pushLog('ClickShare (fail)', 'sin selectedEvent');
      return;
    }
    if (!isObjectId(athleteId)) {
      showToast('❌ athleteId inválido (no es ObjectId).');
      pushLog('ClickShare (fail)', `athleteId inválido: ${String(athleteId || '')}`);
      return;
    }

    showToast(`📨 Enviando "${selectedEvent.title}" a ${name}...`);
    pushLog('ClickShare', `"${selectedEvent.title}" → ${name}`);
    setSharingAthleteId(String(athleteId));

    try {
      const resp = await shareToAthlete(athleteId, selectedEvent);
      showToast(`✅ Compartido: ${selectedEvent.title} → ${name}`);
      pushLog('OK /training/share', `id=${resp?.id || resp?._id || 'ok'}`);
    } catch (err) {
      const msg = String(err?.message || err);
      showToast(`❌ ${msg}`);
      pushLog('ERROR /training/share', msg);
    } finally {
      setSharingAthleteId(null);
    }
  };

  return (
    <div className="sb-root">
      {toast && <div className="sb-toast">{toast}</div>}

      <div className="sb-header">
        <h1 className="sb-title">✅ ShareBoard ACTIVO (Trainer)</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="sb-refresh"
            type="button"
            onClick={() => {
              showToast('✅ Toast OK (botón test)');
              pushLog('TEST', 'toast/log OK');
            }}
          >
            Probar Toast
          </button>
          <button className="sb-refresh" onClick={load} disabled={loading}>
            Refrescar
          </button>
        </div>
      </div>

      {error && <div className="sb-error">{error}</div>}
      {loading && <div className="sb-loading">{t("common.loading")}</div>}

      {/* Debug visible */}
      <div style={{ marginBottom: 10, fontWeight: 800, opacity: 0.9 }}>
        userId:{' '}
        <span style={{ fontFamily: 'monospace' }}>
          {userId || '(vacío)'}
        </span>{' '}
        · atletas: {athletes.length} · seleccionado:{' '}
        <b>{selectedEvent?.title || '—'}</b>
      </div>

      <div className="sb-grid">
        {/* Eventos */}
        <div className="sb-panel">
          <div className="sb-panel-title">{t("shareBoard.availableEvents")}</div>

          <div className="sb-events">
            {events.map((ev) => {
              const active = String(ev.id) === String(selectedEventId);
              return (
                <div
                  key={ev.id}
                  className="sb-event-card"
                  draggable
                  onDragStart={(e) => onDragStartEvent(e, ev)}
                  onClick={() => {
                    setSelectedEventId(ev.id);
                    showToast(`✅ Evento seleccionado: ${ev.title}`);
                    pushLog('SelectEvent', ev.title);
                  }}
                  title={t("shareBoard.dragToAthlete")}
                  style={{
                    outline: active ? '3px solid rgba(255,255,255,0.85)' : 'none',
                    transform: active ? 'translateY(-1px)' : 'none',
                  }}
                >
                  <div className="sb-event-title">{ev.title}</div>
                  <div className="sb-event-sub">
                    {new Date(ev.start).toLocaleString('es-CL')} ·{' '}
                    {ev.planned?.durationMin || 0} min
                  </div>
                  <div className="sb-event-sub">
                    type → <b>{normalizeType(ev.type || ev.sport)}</b>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Atletas */}
        <div className="sb-panel">
          <div className="sb-panel-title">{t("shareBoard.myAthletes")}</div>

          {!loading && athletes.length === 0 ? (
            <div className="sb-empty">{t("shareBoard.noAthletes")}</div>
          ) : (
            <div className="sb-athletes">
              {athletes.map((a, idx) => {
                const key = a.userId || a._id || a.id || String(idx);
                const name =
                  a.displayName || a.name || a.fullName || 'Atleta';
                const aId = a.userId || a._id || a.id;

                const isOver = overId === String(aId);
                const isSharing = sharingAthleteId === String(aId);

                return (
                  <div
                    key={String(key)}
                    className={'sb-athlete-drop' + (isOver ? ' sb-athlete-drop--over' : '')}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setOverId(String(aId));
                      pushLog('DragEnter', name);
                    }}
                    onDragLeave={() => setOverId(null)}
                    onDrop={(e) => onDropOnAthlete(e, a)}
                    title={t("shareBoard.dropEvent")}
                    style={{ opacity: isSharing ? 0.7 : 1 }}
                  >
                    <div className="sb-athlete-name">
                      {name} {isSharing ? ' · Enviando…' : ''}
                    </div>
                    <div className="sb-athlete-sub">
                      ID: {(aId || '').toString().slice(0, 8)}…
                    </div>
                    <div className="sb-athlete-hint">{t("shareBoard.dropEvent")}</div>

                    <div style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        onClick={() => shareSelectedTo(a)}
                        disabled={!selectedEvent || isSharing}
                        style={{
                          border: 0,
                          borderRadius: 10,
                          padding: '8px 10px',
                          cursor: selectedEvent && !isSharing ? 'pointer' : 'not-allowed',
                          fontWeight: 800,
                        }}
                      >
                        Compartir evento seleccionado
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Log visible */}
          <div
            style={{
              marginTop: 12,
              background: 'rgba(255,255,255,0.8)',
              borderRadius: 12,
              padding: 10,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>{t('social.meetupsBoard.log')}</div>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
              }}
            >
              {(logs || []).length ? logs.join('\n') : '— sin eventos aún —'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
